import { useCallback } from 'react';
import { useAppDispatch } from '../../../../reduxService/hooks';
import { convertFlatData } from '../../../services/UnitConversionService';
import { unit as unitEnum } from '../../../enum/project';
import { FieldConfig } from '../../../services/ValidationService';
import { setProject } from '../../store/project/projectslice';
import { logDebug, LogTag } from '../../../utils/utils';
import { ConversionDecision, logConversionDecision } from '../../../utils/conversionUtils';

/**
 * Hook for managing unit conversions
 * Single responsibility: Unit conversion logic and decisions
 */
export const useUnitConversion = () => {
    const dispatch = useAppDispatch();

    const shouldConvert = useCallback((targetUnit: unitEnum, conversionApplied = false): ConversionDecision => {
        // Simple rule: If target is Imperial and data hasn't been converted yet, convert once
        const shouldConvertSimple = targetUnit === unitEnum.Imperial && !conversionApplied;

        const decision: ConversionDecision = {
            conversionNeeded: shouldConvertSimple,
            sourceUnit: unitEnum.SI, // API data is always metric
            targetUnit: targetUnit,
            reason: shouldConvertSimple ? 'Converting metric API data to Imperial display' : 'Data already converted or target is metric',
            confidence: 1.0, // High confidence in simple approach
        };

        logConversionDecision(decision, 'useUnitConversion');
        return decision;
    }, []);

    const performConversion = useCallback((data: any, method: string, fields: FieldConfig[], sourceUnit: unitEnum, targetUnit: unitEnum): any => {
        logDebug('UNIT_CONVERSION', 'Performing conversion', { method, sourceUnit, targetUnit, fieldsCount: fields.length }, LogTag.UNIT_CONVERSION);

        console.log('📐 [UNIT] performConversion called:', {
            method,
            sourceUnit,
            targetUnit,
            fieldsCount: fields.length,
            direction: `${sourceUnit}→${targetUnit}`,
        });

        // FLAT structure: convert data directly without wrapping
        const convertedData = convertFlatData(data, fields, sourceUnit, targetUnit);

        if (convertedData) {
            console.log('✅ [UNIT] performConversion SUCCESS:', {
                method,
                direction: `${sourceUnit}→${targetUnit}`,
                convertedKeysCount: Object.keys(convertedData).length,
            });
            logDebug('UNIT_CONVERSION', `Successfully converted from ${sourceUnit} to ${targetUnit}`, undefined, LogTag.UNIT_CONVERSION);
            return convertedData;
        }

        logDebug('UNIT_CONVERSION', 'Conversion failed, returning original data', undefined, LogTag.UNIT_CONVERSION);
        return data;
    }, []);

    const updateConversionTracking = useCallback(
        (projectData: any, method: string, convertedData: any) => {
            if (!projectData) return;

            console.log('📥 [UNIT] updateConversionTracking - Updating Redux with converted data:', {
                guid: projectData.guid,
                method,
                unit: projectData.unit,
                conversionApplied: true,
                convertedDataKeys: Object.keys(convertedData || {}).slice(0, 10),
            });

            // FLAT structure: merge converted data directly at root level
            const updatedProjectData = {
                ...projectData,
                conversionApplied: true,
                conversionSource: 'useUnitConversion',
                lastModified: new Date().toISOString(),
                projectInfo: {
                    ...projectData.projectInfo,
                    data: {
                        ...projectData.projectInfo.data,
                        ...convertedData, // FLAT: spread converted data at root
                        method: method, // preserve method indicator
                    } as any,
                },
            };

            dispatch(setProject(updatedProjectData));
            logDebug('UNIT_CONVERSION', 'Updated Redux state with conversion tracking', undefined, LogTag.REDUX_STATE);
        },
        [dispatch]
    );

    return {
        shouldConvert,
        performConversion,
        updateConversionTracking,
    };
};

export type UseUnitConversionReturn = ReturnType<typeof useUnitConversion>;
